import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { teacherApi } from '../../services/api';
import HedefAtaModal from '../../components/HedefAtaModal';
import BulkGoalUploadModal from '../../components/BulkGoalUploadModal';
import { dedupSubjectOptionsFromBooks } from '../../utils/subjectOptions';

/**
 * Teacher → "Öğrenci Programı" page.
 *
 * Flow: pick class → pick student → view that student's REAL weekly program
 * (fetched from /teacher/student-program/:uuid which delegates to the student
 * schedule/goals services). "Hedef Oluştur" hits the real
 * /teacher/goals/assign-student endpoint so the goal lands in the student's
 * own Programım page immediately.
 *
 * Previous version rendered a hardcoded scheduleSlots / hedefler list where
 * only Monday had data — that's fixed here.
 */

const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

interface ClassStudent { id: number | string; uuid?: string; name: string; surname: string }
interface ClassRoom { id: number | string; name: string; code: string; students: ClassStudent[]; cover: string }

function mapClass(c: any, idx: number): ClassRoom {
  return {
    id: c.id,
    name: c.campuses?.institutions?.name ?? c.campuses?.name ?? '—',
    code: c.name ?? '',
    students: (c.class_student_enrollments ?? []).map((e: any) => ({
      id: e.users?.id ?? e.id,
      uuid: e.users?.uuid,
      name: e.users?.first_name ?? '',
      surname: e.users?.last_name ?? '',
    })),
    cover: `/a-sinif-tiling${(idx % 4) + 1}.png`,
  };
}

interface ScheduleEntry {
  id: number | string;
  day_of_week: number;
  start_time?: string | null;
  end_time?: string | null;
  title?: string | null;
  topic?: string | null;
  subject_id?: number | null;
  question_target?: number | null;
  target_questions?: number | null;
  duration_minutes?: number | null;
  subjects?: { id?: number; name?: string };
  // For schedule_entries the backend already returns this; for goal-derived
  // pseudo-entries we map it from g.status === 'completed'.
  is_completed?: boolean;
  __isGoal?: boolean;
  __goalStatus?: string;
  __goal?: Goal;
}

interface Goal {
  id: number | string | bigint;
  target_value?: number;
  achieved_value?: number;
  correct_count?: number;
  wrong_count?: number;
  accuracy_rate?: number | string;
  start_time?: string | null;
  end_time?: string | null;
  goal_date?: string | null;
  status?: string;
  // T18/T19 — who created the goal + which window it spans. Drives the
  // "Öğrenci Hedefi" / "Öğretmen Hedefi" badges so teachers can tell a
  // student's own goal from an assigned one at a glance.
  created_by_type?: string;
  period_type?: 'daily' | 'weekly' | 'monthly' | string;
  period_start?: string | null;
  period_end?: string | null;
  auto_tracked?: boolean;
  goal_definitions?: { id?: number; title?: string; subjects?: { id?: number; name?: string } | null };
  subject_units?: { name?: string; subjects?: { name?: string } };
}

const PERIOD_LABEL: Record<string, string> = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };

// "15 Tem" style; weekly/monthly show the start–end range (same as student view).
function goalPeriodRange(g: { period_type?: string; period_start?: string | null; period_end?: string | null; goal_date?: string | null }): string {
  const fmt = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getUTCDate()} ${d.toLocaleDateString('tr-TR', { month: 'short', timeZone: 'UTC' })}`;
  };
  const start = g.period_start ?? g.goal_date;
  const end = g.period_end ?? g.goal_date;
  if (!start) return '';
  if (!end || start === end || g.period_type === 'daily') return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

// Badge colors: student-created goals amber, teacher-assigned pink — the
// pill text alone already says it, the color just makes scanning faster.
const creatorBadge = (g: Goal) =>
  g.created_by_type === 'student'
    ? { text: 'Öğrenci Hedefi', bg: '#e8930c' }
    : { text: 'Öğretmen Hedefi', bg: '#e5599b' };

function fmtTimeRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return '—';
  const f = (s?: string | null) => {
    if (!s) return '—';
    const d = new Date(s);
    return d.getUTCHours().toString().padStart(2, '0') + ':' + d.getUTCMinutes().toString().padStart(2, '0');
  };
  return `${f(start)} - ${f(end)}`;
}

function computeDuration(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  const mins = Math.round((e.getTime() - s.getTime()) / 60000);
  return mins > 0 ? mins : null;
}

const F = { fontFamily: 'Inter' } as const;

type Step = 'select-class' | 'select-student' | 'schedule';
type View = 'program' | 'hedef';
type Modal = null | 'hedef-olustur' | 'kaydedildi' | 'error' | 'confirm-delete' | 'future-confirm';
type DeleteTarget = { kind: 'entry' | 'goal'; id: string } | null;
type FutureConfirmTarget = { slot: ScheduleEntry; targetDate: Date } | null;

// Week helpers — Monday-anchored, same logic as student/Schedule.tsx
function weekStartForOffset(offsetWeeks: number): Date {
  const d = new Date();
  const jsDow = d.getDay();
  const diff = (jsDow + 6) % 7;
  d.setDate(d.getDate() - diff + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}
function toLocalIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const fmt = (x: Date) => `${x.getDate()} ${x.toLocaleDateString('tr-TR', { month: 'short' })}`;
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

const TeacherSchedule: React.FC = () => {
  const [classrooms, setClassrooms] = useState<ClassRoom[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [step, setStep] = useState<Step>('select-class');
  const [selectedClassId, setSelectedClassId] = useState<number | string>(0);
  const [selectedStudent, setSelectedStudent] = useState<ClassStudent | null>(null);
  const [bulkGoalTarget, setBulkGoalTarget] = useState<{ type: 'class' | 'student'; id: string; name: string } | null>(null);
  const [view, setView] = useState<View>('program');
  // Week navigation — 0=this week, +1=next, -1=last
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(() => weekStartForOffset(weekOffset), [weekOffset]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);
  // Bulk class goal assignment
  const [showClassGoalModal, setShowClassGoalModal] = useState(false);

  // Real program data (per-student)
  const [schedules, setSchedules] = useState<any[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  // Pending toggles — keyed by `${kind}-${id}`. Used to disable the click
  // while the request is in flight so a second tap can't double-toggle.
  const [togglePending, setTogglePending] = useState<Set<string>>(new Set());
  const [loadingProgram, setLoadingProgram] = useState(false);

  // Goal creation moved into HedefAtaModal (Phase 13). What remains here:
  // modal-state for the delete confirm dialog + a tiny formData record so
  // the day-grid "+" button can seed the modal with the clicked weekday.
  const [modal, setModal] = useState<Modal>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [futureConfirmTarget, setFutureConfirmTarget] = useState<FutureConfirmTarget>(null);
  const [formData, setFormData] = useState({
    goalDate: new Date().toISOString().slice(0, 10),
    startTime: '10:00', endTime: '12:00',
  });

  // ---- Load classes ----
  useEffect(() => {
    teacherApi.classes()
      .then((res) => {
        const mapped = (res.data || []).map(mapClass);
        setClassrooms(mapped);
      })
      .catch(console.error)
      .finally(() => setLoadingClasses(false));
  }, []);

  const cls = classrooms.find(c => c.id === selectedClassId) || classrooms[0];

  // ---- Load student program when student is picked ----
  const loadProgram = useCallback((studentUuid: string) => {
    setLoadingProgram(true);
    teacherApi.studentProgram(studentUuid)
      .then((res) => {
        setSchedules(res.data?.schedule ?? []);
        setGoals((res.data?.goals ?? []) as Goal[]);
      })
      .catch(console.error)
      .finally(() => setLoadingProgram(false));
  }, []);

  useEffect(() => {
    if (step === 'schedule' && selectedStudent?.uuid) loadProgram(selectedStudent.uuid);
  }, [step, selectedStudent, loadProgram]);

  // Goal definitions are now loaded by HedefAtaModal directly when it opens,
  // so this page no longer fetches them on mount.

  // ---- Flatten entries + goals into one per-day bucket (date-based) ----
  const allEntries: ScheduleEntry[] = schedules.flatMap((s: any) => s.study_schedule_entries ?? []);
  const weekDayIsos = weekDays.map(toLocalIso);

  // entriesByDay is keyed by weekday index (0=Mon..6=Sun) for this week
  const entriesByDay: Record<number, ScheduleEntry[]> = {};
  for (let i = 0; i < 7; i++) entriesByDay[i] = [];

  // Schedule entries repeat by day_of_week — show on matching weekday
  for (const e of allEntries) {
    if (e.day_of_week >= 0 && e.day_of_week <= 6) entriesByDay[e.day_of_week].push(e);
  }

  // Goals: only show goals whose goal_date falls within the selected week
  for (const g of goals) {
    if (!g.goal_date) continue;
    // Weekly/monthly goals without saat don't fit a single day column —
    // they render as the progress band under the grid (parity with the
    // student's Program Görünümü).
    const isMultiDay = g.period_type === 'weekly' || g.period_type === 'monthly';
    if (isMultiDay && !(g.start_time && g.end_time)) continue;
    const goalIso = String(g.goal_date).slice(0, 10);
    const dayIdx = weekDayIsos.indexOf(goalIso);
    if (dayIdx === -1) continue; // not in this week
    const title = g.goal_definitions?.subjects?.name || g.goal_definitions?.title || 'Hedef';
    entriesByDay[dayIdx].push({
      id: `goal-${g.id}`,
      day_of_week: dayIdx,
      start_time: typeof g.start_time === 'string' ? g.start_time : null,
      end_time: typeof g.end_time === 'string' ? g.end_time : null,
      title,
      question_target: g.target_value,
      subject_id: (g.goal_definitions?.subjects as any)?.id ?? null,
      subjects: g.goal_definitions?.subjects ? { id: (g.goal_definitions.subjects as any).id, name: g.goal_definitions.subjects.name } : undefined,
      is_completed: g.status === 'completed',
      __isGoal: true,
      __goalStatus: g.status,
      __goal: g,
    });
  }
  // Sort each day by start time
  for (const k of Object.keys(entriesByDay)) {
    entriesByDay[Number(k)].sort((a, b) => {
      const at = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bt = b.start_time ? new Date(b.start_time).getTime() : 0;
      return at - bt;
    });
  }

  // ---- Phase 11: toggle the green check on a slot ----
  // For future-dated goals, show a confirmation popup before marking complete.
  const handleToggleClick = (slot: ScheduleEntry, dayIdx: number) => {
    if (!slot.is_completed) {
      // Check if this slot's date is in the future. Clone the Date instance
      // so we don't mutate weekDays[dayIdx] — that would shift the rendered
      // grid by 12 hours on every click.
      const orig = weekDays[dayIdx];
      const slotDate = new Date(orig.getFullYear(), orig.getMonth(), orig.getDate(), 12, 0, 0, 0);
      const todayNoon = new Date();
      todayNoon.setHours(12, 0, 0, 0);
      if (slotDate > todayNoon) {
        setFutureConfirmTarget({ slot, targetDate: orig });
        setModal('future-confirm');
        return;
      }
    }
    toggleSlotCompletion(slot);
  };

  const toggleSlotCompletion = async (slot: ScheduleEntry) => {
    if (!selectedStudent?.uuid) return;
    const key = `${slot.__isGoal ? 'goal' : 'entry'}-${slot.id}`;
    if (togglePending.has(key)) return;
    setTogglePending((p) => new Set(p).add(key));
    const next = !slot.is_completed;
    try {
      if (slot.__isGoal) {
        const goalId = String(slot.id).replace(/^goal-/, '');
        await teacherApi.setGoalCompletion(selectedStudent.uuid, goalId, next);
        setGoals((prev) => prev.map((g) =>
          String(g.id) === goalId ? { ...g, status: next ? 'completed' : 'active' } : g
        ));
      } else {
        await teacherApi.setScheduleEntryCompletion(selectedStudent.uuid, String(slot.id), next);
        setSchedules((prev) => prev.map((s: any) => ({
          ...s,
          study_schedule_entries: (s.study_schedule_entries ?? []).map((e: any) =>
            String(e.id) === String(slot.id) ? { ...e, is_completed: next } : e
          ),
        })));
      }
      // Reload from server so DB truth wins (other-tab edits, etc.).
      loadProgram(selectedStudent.uuid);
    } catch (e) {
      // Roll back the optimistic change by reloading from server.
      loadProgram(selectedStudent.uuid);
    } finally {
      setTogglePending((p) => { const n = new Set(p); n.delete(key); return n; });
    }
  };

  // Phase 13 — submit logic moved into HedefAtaModal. The page just opens
  // the modal with a seeded date when the user clicks the day's "+" button.

  // Delete confirmed entry / goal
  const askDelete = (kind: 'entry' | 'goal', id: string) => {
    setDeleteTarget({ kind, id });
    setFormError(null);
    setModal('confirm-delete');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !selectedStudent?.uuid) return;
    setSubmitting(true); setFormError(null);
    try {
      if (deleteTarget.kind === 'entry') {
        await teacherApi.deleteStudentScheduleEntry(selectedStudent.uuid, deleteTarget.id);
      } else {
        await teacherApi.deleteStudentGoal(selectedStudent.uuid, deleteTarget.id);
      }
      loadProgram(selectedStudent.uuid);
      setModal(null);
      setDeleteTarget(null);
    } catch (e: any) {
      setFormError(e?.response?.data?.error ?? e?.message ?? 'Silinemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- SELECT CLASS ----------
  if (step === 'select-class') return (
    <div>
      <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b', marginBottom: 20 }}>Sınıf Seçimi</h1>
      {loadingClasses ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : classrooms.length === 0 ? (
        <p className="text-center text-gray-400 py-16">Henüz öğretmeni olduğunuz bir sınıf yok.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {classrooms.map(c => (
            <div key={c.id} onClick={() => { setSelectedClassId(c.id); setStep('select-student'); }}
              className="bg-[#fafafa] rounded-[16px] overflow-hidden cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <div className="h-[106px] overflow-hidden"><img src={c.cover} alt="" className="w-full h-full object-cover object-center" /></div>
              <div className="p-4">
                <p style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{c.name}</p>
                <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', marginTop: 4 }}>{c.code}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <svg width="18" height="15" viewBox="0 0 24 20" fill="#2b2b2b"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>
                  <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{c.students.length} Öğrenci</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ---------- SELECT STUDENT ----------
  if (step === 'select-student') return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button onClick={() => setStep('select-class')} className="hover:bg-gray-100 rounded-full p-1 transition-colors">
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
        </button>
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenciler — {cls?.students.length ?? 0}</h1>
        <button
          onClick={() => setShowClassGoalModal(true)}
          className="ml-auto bg-[#e5599b] text-white text-[13px] xl:text-[15px] font-bold px-5 py-2.5 rounded-[12px] hover:opacity-90 flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
          Sınıfa Hedef Ata
        </button>
        <button
          onClick={() => cls && setBulkGoalTarget({ type: 'class', id: String(cls.id), name: cls.code })}
          className="bg-white border-2 border-[#59e56e] text-[#179e3b] text-[13px] xl:text-[15px] font-bold px-5 py-2.5 rounded-[12px] hover:bg-green-50 flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          Toplu Hedef Atama (.xlsx)
        </button>
      </div>
      {bulkGoalTarget && (
        <BulkGoalUploadModal
          target={bulkGoalTarget}
          onClose={() => setBulkGoalTarget(null)}
          onDone={() => { if (selectedStudent?.uuid) loadProgram(selectedStudent.uuid); }}
        />
      )}
      {/* Bulk class goal modal */}
      {showClassGoalModal && cls && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowClassGoalModal(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[560px] p-6 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowClassGoalModal(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><line x1="4" y1="4" x2="20" y2="20"/><line x1="20" y1="4" x2="4" y2="20"/></svg>
            </button>
            <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#e5599b', marginBottom: 8 }}>Sınıfa Hedef Ata</h2>
            <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 16 }}>
              Hedef <strong>{cls.code}</strong> sınıfındaki tüm öğrencilere atanacak. Saat çakışması olan öğrenciler atlanır.
            </p>
            <ClassBulkGoalForm
              classId={String(cls.id)}
              onClose={() => setShowClassGoalModal(false)}
              onAssigned={() => { if (selectedStudent?.uuid) loadProgram(selectedStudent.uuid); }}
            />
          </div>
        </div>
      )}
      {cls && (
        <div className="bg-[#fafafa] rounded-[20px] flex overflow-hidden mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <div className="w-[257px] h-[257px] flex-shrink-0 overflow-hidden"><img src={cls.cover} alt="" className="w-full h-full object-cover" /></div>
          <div className="flex-1 p-5 flex flex-col">
            <p style={{ ...F, fontSize: 32, fontWeight: 500, color: '#2b2b2b' }}>{cls.name}</p>
            <p style={{ ...F, fontSize: 24, fontWeight: 500, color: '#2b2b2b' }}>{cls.code} — {cls.students.length} Öğrenci</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        {(cls?.students ?? []).map(s => (
          <div key={s.id} onClick={() => { setSelectedStudent(s); setStep('schedule'); }}
            className="bg-[#fafafa] rounded-[20px] p-5 flex flex-col items-center cursor-pointer hover:shadow-md transition-shadow" style={{ width: 280, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <div className="w-[70px] h-[70px] rounded-full bg-[#3973bf] overflow-hidden mb-3"><img src="/a-avatar.png" alt="" className="w-full h-full object-cover" /></div>
            <p style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>{s.name}</p>
            <p style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>{s.surname}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // ---------- SCHEDULE VIEW ----------
  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={() => { setStep('select-student'); setSelectedStudent(null); }} className="hover:bg-gray-100 rounded-full p-1 transition-colors">
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
        </button>
        <h1 className="text-[18px] xl:text-[24px] font-semibold text-[#2b2b2b]">
          {selectedStudent?.name} {selectedStudent?.surname} — Programı
        </h1>

        <button onClick={() => setView('program')}
          className={`px-4 xl:px-5 py-2 xl:py-2.5 rounded-[12px] text-[12px] xl:text-[16px] font-bold transition-colors ${view === 'program' ? 'bg-[#e5599b] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}>
          Program Görünümü
        </button>
        <button onClick={() => setView('hedef')}
          className={`px-4 xl:px-5 py-2 xl:py-2.5 rounded-[12px] text-[12px] xl:text-[16px] font-bold transition-colors ${view === 'hedef' ? 'bg-[#e5599b] text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}>
          Hedef Görünümü
        </button>

        <button
          onClick={() => { setFormError(null); setModal('hedef-olustur'); }}
          className="ml-auto bg-[#3973bf] text-white text-[13px] xl:text-[15px] font-bold px-5 py-2.5 rounded-[12px] hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>
          Hedef Ata
        </button>
        <button
          onClick={() => selectedStudent && setBulkGoalTarget({ type: 'student', id: selectedStudent.uuid ?? String(selectedStudent.id), name: `${selectedStudent.name} ${selectedStudent.surname}`.trim() })}
          className="bg-white border-2 border-[#59e56e] text-[#179e3b] text-[13px] xl:text-[15px] font-bold px-5 py-2.5 rounded-[12px] hover:bg-green-50 flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          Toplu Hedef (.xlsx)
        </button>
      </div>
      {bulkGoalTarget && (
        <BulkGoalUploadModal
          target={bulkGoalTarget}
          onClose={() => setBulkGoalTarget(null)}
          onDone={() => { if (selectedStudent?.uuid) loadProgram(selectedStudent.uuid); }}
        />
      )}

      {/* Haftalık navigasyon */}
      {view === 'program' && (
        <div className="bg-[#fafafa] rounded-[16px] p-3 xl:p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
          <button onClick={() => setWeekOffset(o => o - 1)} aria-label="Önceki hafta"
            className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <div className="text-center flex-1 min-w-[180px]">
            <p className="text-[12px] text-gray-500">
              {weekOffset === 0 ? 'Bu Hafta' : weekOffset < 0 ? `${Math.abs(weekOffset)} hafta önce` : `${weekOffset} hafta sonra`}
            </p>
            <p className="text-[15px] xl:text-[17px] font-bold text-[#2b2b2b]">{formatWeekRange(weekStart)}</p>
          </div>
          <button onClick={() => setWeekOffset(o => o + 1)} aria-label="Sonraki hafta"
            className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)} className="bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] hover:bg-blue-700">
              Bu Haftaya Dön
            </button>
          )}
        </div>
      )}

      {loadingProgram ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : view === 'program' ? (
        <>
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-7 gap-2 mb-2">
              {DAY_NAMES.map((day, i) => {
                const d = weekDays[i];
                const todayIso = toLocalIso(new Date());
                const isToday = toLocalIso(d) === todayIso;
                return (
                  <div key={i} className="text-center">
                    <div className={`rounded-[12px] py-2 xl:py-2.5 ${isToday ? 'bg-[#3973bf]' : 'bg-[#e5599b]'}`}>
                      <span className="block text-[11px] xl:text-[14px] font-semibold text-white">{day}</span>
                      <span className="block text-[10px] xl:text-[12px] text-white/90">{d.getDate()} {d.toLocaleDateString('tr-TR', { month: 'short' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {DAY_NAMES.map((_, dayIdx) => {
                const entries = entriesByDay[dayIdx] ?? [];
                return (
                  <div key={dayIdx} className="space-y-2 min-h-[400px]">
                    {entries.length === 0 ? (
                      <div className="bg-[#fafafa] rounded-[12px] p-3 text-center text-[12px] text-gray-400 border border-gray-100">Boş</div>
                    ) : entries.map((slot, si) => {
                      const color = si % 2 === 0 ? '#3973bf' : '#e5599b';
                      const goalId = slot.__isGoal ? String(slot.id).replace(/^goal-/, '') : null;
                      const completed = !!slot.is_completed;
                      const toggleKey = `${slot.__isGoal ? 'goal' : 'entry'}-${slot.id}`;
                      const toggling = togglePending.has(toggleKey);
                      // Green when completed (matches the green dot on the
                      // student's Programım card). Slight tint of the same
                      // green so the existing blue/pink title text stays
                      // legible.
                      const cardBg = completed ? '#dff5e0' : '#fafafa';
                      const cardBorder = completed ? '2px solid #11D600' : '1px solid transparent';
                      return (
                        <div key={slot.id}
                          className="rounded-[16px] relative overflow-hidden flex flex-col transition-colors"
                          style={{ backgroundColor: cardBg, minHeight: 140, boxShadow: '0px 1px 4px rgba(0,0,0,0.08)', border: cardBorder }}>
                          {/* Delete × — top-right, floats over content. */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (slot.__isGoal && goalId) askDelete('goal', goalId);
                              else askDelete('entry', String(slot.id));
                            }}
                            aria-label="Sil"
                            className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white/90 hover:bg-red-100 flex items-center justify-center text-[14px] text-[#2b2b2b] hover:text-red-600 leading-none"
                            style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.15)' }}
                          >×</button>
                          {/* Phase 12 — completion toggle anchored to the
                              bottom-right so it sits on the same X column as
                              the delete × (right:6 + w:24 → matching centre)
                              and on the same Y as the hour line (bottom:12,
                              hour text is mt-auto inside p-3). The toggle no
                              longer crowds the title at the top of the card. */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleClick(slot, dayIdx); }}
                            disabled={toggling}
                            aria-label={completed ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}
                            title={completed ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}
                            className="absolute right-1.5 bottom-2.5 z-10 w-6 h-6 rounded-full flex items-center justify-center disabled:opacity-50"
                            style={{
                              background: completed ? '#11D600' : '#ffffff',
                              border: `2px solid ${completed ? '#0DA600' : '#3973bf'}`,
                              boxShadow: '0px 1px 2px rgba(0,0,0,0.18)',
                              cursor: toggling ? 'wait' : 'pointer',
                            }}
                          >
                            {completed && (
                              <svg width="11" height="9" viewBox="0 0 10 8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 4l3 3 5-6" />
                              </svg>
                            )}
                          </button>
                          {/* Content — goal badge lives in the normal flow (not absolute)
                              so it can't collide with the title or the × button. */}
                          <div className="p-2.5 xl:p-3 flex-1 flex flex-col" style={{ paddingRight: 32 }}>
                            {/* T18/T19 — the pill used to say a bare "Hedef";
                                now it names the creator so a student's own
                                goal is obvious, plus the period for
                                haftalık/aylık windows. */}
                            {slot.__isGoal && (
                              <span className="flex items-center gap-1 flex-wrap self-start mb-1">
                                <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: creatorBadge(slot.__goal ?? {} as Goal).bg, color: '#fff', ...F }}>
                                  {creatorBadge(slot.__goal ?? {} as Goal).text}
                                </span>
                                {(slot.__goal?.period_type === 'weekly' || slot.__goal?.period_type === 'monthly') && (
                                  <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: '#3973bf', color: '#fff', ...F }}>
                                    {PERIOD_LABEL[slot.__goal.period_type]}
                                  </span>
                                )}
                              </span>
                            )}
                            {(slot.title ?? slot.topic) && <p style={{ color, fontFamily: 'Inter', fontSize: 18, fontWeight: 700, wordBreak: 'break-word' }}>{(slot.title ?? slot.topic)}</p>}
                            {/* Goal pseudo-entries use the subject as their
                                title — don't print the same word twice. */}
                            {slot.subjects?.name && slot.subjects.name !== (slot.title ?? slot.topic) && <p style={{ color, fontFamily: 'Inter', fontSize: 15, fontWeight: 600 }}>{slot.subjects.name}</p>}
                            {(() => {
                              // Goal cards show progress on the bar below —
                              // a separate "N Soru" line would duplicate it.
                              if (slot.__isGoal) return null;
                              const q = slot.question_target ?? slot.target_questions;
                              return q != null ? <p style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{q} Soru</p> : null;
                            })()}
                            {(() => {
                              const dur = slot.duration_minutes ?? computeDuration(slot.start_time, slot.end_time);
                              if (dur == null) return null;
                              const hours = Math.round((dur / 60) * 10) / 10;
                              return <p style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{hours} Saat</p>;
                            })()}
                            {/* Live progress on goal cards — same visual as
                                the student's Program Görünümü. */}
                            {slot.__isGoal && slot.__goal && (() => {
                              const t = slot.__goal.target_value ?? 0;
                              const a = slot.__goal.achieved_value ?? 0;
                              const pct = t > 0 ? Math.min(100, Math.round((a / t) * 100)) : 0;
                              const done = t > 0 && a >= t;
                              const barColor = done ? '#2fae4e' : color;
                              return (
                                <div className="mt-1">
                                  <div className="w-full h-[6px] bg-[#e0e0e0] rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                                  </div>
                                  <p style={{ color: barColor, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                                    {a} / {t} Soru{slot.__goal.auto_tracked ? ' ⚡' : ''}
                                  </p>
                                </div>
                              );
                            })()}
                            {(slot.start_time || slot.end_time) && (
                              <p className="mt-auto" style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 700 }}>{fmtTimeRange(slot.start_time, slot.end_time)}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        // Seed with the actual calendar date for this weekday in the selected week
                        const dayDate = weekDays[dayIdx];
                        setFormData(f => ({ ...f, goalDate: toLocalIso(dayDate) }));
                        setFormError(null);
                        setModal('hedef-olustur');
                      }}
                      className="w-full bg-[#fafafa] rounded-[12px] h-[36px] xl:h-[42px] flex items-center justify-center border border-gray-200 hover:bg-gray-100 transition-colors"
                      aria-label={`${DAY_NAMES[dayIdx]} için hedef ata`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c5c5c5" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Parity with the student's Program Görünümü (T11): weekly/monthly
            goals overlapping the visible week render as progress bars under
            the grid — they don't fit a single day column. */}
        {(() => {
          const spanning = goals.filter((g) => {
            if (g.period_type !== 'weekly' && g.period_type !== 'monthly') return false;
            const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
            const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
            if (!s || !e) return false;
            return s <= weekDayIsos[6] && e >= weekDayIsos[0];
          });
          if (spanning.length === 0) return null;
          return (
            <div className="mt-4">
              <h2 className="text-[15px] xl:text-[18px] font-semibold text-[#2b2b2b] mb-2">Bu Haftayı Kapsayan Hedefler</h2>
              <div className="space-y-2">
                {spanning.map((g) => {
                  const target = g.target_value ?? 0;
                  const achieved = g.achieved_value ?? 0;
                  const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                  const done = target > 0 && achieved >= target;
                  const badge = creatorBadge(g);
                  const c = done ? '#2fae4e' : g.period_type === 'monthly' ? '#e5599b' : '#3973bf';
                  const subject = g.goal_definitions?.subjects?.name || g.goal_definitions?.title || 'Genel';
                  return (
                    <div
                      key={String(g.id)}
                      onClick={() => setView('hedef')}
                      title="Hedef Görünümünde aç"
                      className="bg-[#fafafa] rounded-[16px] p-3 xl:p-4 cursor-pointer hover:shadow-md transition-shadow"
                      style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: badge.bg, ...F }}>{badge.text}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c }}>{PERIOD_LABEL[g.period_type ?? ''] ?? 'Hedef'}</span>
                        <span className="text-[13px] xl:text-[15px] font-bold" style={{ color: c }}>{subject}</span>
                        <span className="text-[11px] xl:text-[12px] font-semibold text-gray-500">{goalPeriodRange(g)}</span>
                        {done && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#2fae4e' }}>Tamamlandı</span>
                        )}
                        {g.auto_tracked && <span className="text-[11px] text-gray-400 ml-auto">⚡ Otomatik takip</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[8px] bg-[#e0e0e0] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: c }} />
                        </div>
                        <span className="text-[12px] xl:text-[14px] font-semibold whitespace-nowrap" style={{ color: c }}>
                          {achieved} / {target} Soru (%{pct})
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        </>
      ) : (
        // Hedef Görünümü — goals filtered to selected week
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[16px] xl:text-[20px] font-semibold text-[#2b2b2b]">
              Hedefler — {weekOffset === 0 ? 'Bu Hafta' : weekOffset < 0 ? `${Math.abs(weekOffset)} hafta önce` : `${weekOffset} hafta sonra`}
            </h2>
            <div className="flex gap-2">
              <button onClick={() => setWeekOffset(o => o - 1)} className="w-8 h-8 rounded-full bg-[#fafafa] hover:bg-gray-100 flex items-center justify-center border border-gray-200">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round"><path d="M13 4l-6 6 6 6"/></svg>
              </button>
              {weekOffset !== 0 && (
                <button onClick={() => setWeekOffset(0)} className="text-[12px] font-semibold text-[#3973bf] px-3 py-1 rounded-[8px] bg-blue-50 hover:bg-blue-100">Bu Hafta</button>
              )}
              <button onClick={() => setWeekOffset(o => o + 1)} className="w-8 h-8 rounded-full bg-[#fafafa] hover:bg-gray-100 flex items-center justify-center border border-gray-200">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round"><path d="M7 4l6 6-6 6"/></svg>
              </button>
            </div>
          </div>
          {(() => {
            // Filter goals to those touching the selected week. Day-anchored
            // goals match on goal_date; weekly/monthly student goals match
            // when their period window overlaps the week (goal_date alone
            // would hide e.g. an aylık hedef in its second week).
            const weekGoals = goals.filter(g => {
              const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
              const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
              if (!s || !e) return false;
              return s <= weekDayIsos[6] && e >= weekDayIsos[0];
            });
            if (weekGoals.length === 0) return (
              <p className="text-center text-gray-400 py-10">Bu haftaya ait hedef yok.</p>
            );
            return (
            <div className="space-y-3">
              {weekGoals.map((g) => {
                const target = g.target_value ?? 0;
                const achieved = g.achieved_value ?? 0;
                const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
                const label =
                  [g.subject_units?.subjects?.name, g.subject_units?.name].filter(Boolean).join(' - ')
                  || g.goal_definitions?.subjects?.name
                  || g.goal_definitions?.title
                  || 'Hedef';
                const c = '#3973bf';
                return (
                  <div key={String(g.id)} className="rounded-[24px] overflow-hidden relative" style={{ background: '#fafafa', boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
                    <button
                      onClick={() => askDelete('goal', String(g.id))}
                      aria-label="Hedefi sil"
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/90 hover:bg-red-100 flex items-center justify-center text-[16px] text-[#2b2b2b] hover:text-red-600 leading-none"
                      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.15)' }}
                    >×</button>
                    <div className="flex">
                      <div className="relative overflow-hidden p-3 xl:p-4 flex-1" style={{ minHeight: 140 }}>
                        {/* T18/T19 — creator + period badges, same semantics
                            as the program-grid cards. */}
                        <div className="flex items-center gap-1.5 flex-wrap mb-1 pr-8">
                          <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: creatorBadge(g).bg, ...F }}>
                            {creatorBadge(g).text}
                          </span>
                          <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: '#3973bf', ...F }}>
                            {PERIOD_LABEL[g.period_type ?? 'daily'] ?? 'Günlük'}
                          </span>
                          {g.auto_tracked && <span className="text-[11px] text-gray-400">⚡ otomatik takip</span>}
                        </div>
                        <p className="text-[14px] xl:text-[16px] font-bold" style={{ color: c }}>{label}</p>
                        {(g.start_time || g.end_time) && (
                          <p className="text-[13px] xl:text-[15px] font-semibold mt-0.5" style={{ color: c }}>{fmtTimeRange(g.start_time, g.end_time)}</p>
                        )}
                        <p className="text-[12px] mt-0.5" style={{ color: '#888' }}>
                          {(() => {
                            const fmt = (v?: string | null) => {
                              if (!v) return '';
                              const d = new Date(v);
                              return isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR', { timeZone: 'UTC' });
                            };
                            const s = fmt(g.period_start ?? g.goal_date);
                            const e = fmt(g.period_end ?? g.goal_date);
                            return s && e && s !== e ? `${s} – ${e}` : s;
                          })()}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <div className="flex-1 h-[6px] bg-[#c5c5c5] rounded-full">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: c }} />
                          </div>
                          <span className="text-[12px] xl:text-[14px] font-semibold" style={{ color: c }}>{achieved} / {target}</span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 px-3 xl:px-5 py-3 flex flex-col justify-center gap-2 w-[130px] border-l border-gray-200" style={{ borderLeftColor: c }}>
                        <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Doğru</span><span className="text-[15px] font-bold" style={{ color: c }}>{g.correct_count ?? 0}</span></div>
                        <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Yanlış</span><span className="text-[15px] font-bold" style={{ color: c }}>{g.wrong_count ?? 0}</span></div>
                        <div className="flex items-baseline justify-between"><span className="text-[13px] font-semibold" style={{ color: c }}>Başarı</span><span className="text-[15px] font-bold" style={{ color: c }}>%{g.accuracy_rate ?? 0}</span></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>
      )}

      {/* Phase 13 — replaced the inline simple form with the shared
          HedefAtaModal so this page matches Sınıflarım > Hedef Ata feature
          for feature (Test + Topic dropdowns + optional homework). */}
      <HedefAtaModal
        open={modal === 'hedef-olustur' && !!selectedStudent?.uuid}
        studentUuid={selectedStudent?.uuid ?? ''}
        studentName={selectedStudent ? `${selectedStudent.name} ${selectedStudent.surname}`.trim() : ''}
        defaultDate={formData.goalDate}
        defaultStartTime={formData.startTime}
        defaultEndTime={formData.endTime}
        onClose={() => setModal(null)}
        onSubmitted={() => {
          setModal(null);
          if (selectedStudent?.uuid) loadProgram(selectedStudent.uuid);
        }}
      />

      {modal === 'future-confirm' && futureConfirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setModal(null); setFutureConfirmTarget(null); }} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[420px] p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a97e00" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <p className="text-[16px] font-semibold text-[#2b2b2b] mb-2">Bu hedef gelecek haftaya ait!</p>
            <p className="text-[13px] text-gray-500 mb-1">
              <strong>Tarih:</strong> {futureConfirmTarget.targetDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
            <p className="text-[13px] text-gray-500 mb-5">
              Bu hedefi <strong>şimdiden tamamlandı</strong> olarak işaretlemek istediğinizden emin misiniz?
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setModal(null); setFutureConfirmTarget(null); }}
                className="bg-gray-100 text-[#2b2b2b] text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-gray-200">
                Vazgeç
              </button>
              <button
                onClick={() => {
                  const slot = futureConfirmTarget.slot;
                  setModal(null);
                  setFutureConfirmTarget(null);
                  toggleSlotCompletion(slot);
                }}
                className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-blue-700">
                Evet, tamamlandı olarak işaretle
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'confirm-delete' && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { setModal(null); setDeleteTarget(null); }} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[400px] p-6 text-center">
            <p className="text-[16px] font-semibold text-[#2b2b2b] mb-2">Silmek istediğinize emin misiniz?</p>
            <p className="text-[13px] text-gray-500 mb-6">
              {deleteTarget.kind === 'goal' ? 'Bu hedef öğrencinin programından kaldırılacak.' : 'Bu program kaydı öğrencinin programından kaldırılacak.'} Bu işlem geri alınamaz.
            </p>
            {formError && <p className="text-[13px] text-red-500 mb-3">{formError}</p>}
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setModal(null); setDeleteTarget(null); setFormError(null); }}
                className="bg-gray-100 text-[#2b2b2b] text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-gray-200">
                Vazgeç
              </button>
              <button onClick={confirmDelete} disabled={submitting}
                className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-2.5 rounded-[12px] hover:bg-pink-600 disabled:opacity-60">
                {submitting ? 'Siliniyor…' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'kaydedildi' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-20" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[16px] px-10 py-6 text-center">
            <p className="text-[18px] font-bold text-[#3973bf]">Hedef Başarıyla Atandı</p>
          </div>
        </div>
      )}
      {modal === 'error' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-20" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-[16px] px-8 py-6 text-center max-w-[420px]">
            <p className="text-[15px] font-semibold text-red-500 mb-3">{formError ?? 'Bir hata oluştu.'}</p>
            <button onClick={() => setModal(null)} className="rounded-[10px] px-6 py-2 bg-[#3973bf] text-white text-[14px] font-semibold">Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSchedule;

// ── Sınıfa Toplu Hedef Ata formu ──────────────────────────────────────────
interface BulkResult { succeeded: number; conflicts: { student: string; message: string }[] }

const ClassBulkGoalForm: React.FC<{ classId: string; onClose: () => void; onAssigned?: () => void }> = ({ classId, onClose, onAssigned }) => {
  const [goalDefs, setGoalDefs] = useState<{ id: number; title: string }[]>([]);
  const [goalDefId, setGoalDefId] = useState<number | ''>('');
  const [target, setTarget] = useState('20');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('19:00');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  // Parity with the single-student HedefAtaModal: Ders & Konu.
  // selection + optional book homework. Backend assignGoalToClass already
  // accepts subjectUnitId / subjectId / homework.
  const [books, setBooks] = useState<Array<{ id: number; title: string; display_name?: string | null; subjectId?: number | null; subject?: { id?: number; name?: string } | null }>>([]);
  const [attachHw, setAttachHw] = useState(false);
  const [bookId, setBookId] = useState<number | ''>('');
  const [sectionLabel, setSectionLabel] = useState('');
  const [sectionOptions, setSectionOptions] = useState<Array<{ section_label: string; section_display_label?: string | null; section_category?: string | null; question_count?: number }>>([]);
  const [kazanims, setKazanims] = useState<Array<{ id: number; title: string }>>([]);
  const [subjectUnitId, setSubjectUnitId] = useState<number | ''>('');
  const [standaloneSubjectId, setStandaloneSubjectId] = useState<number | ''>('');
  const [standaloneKazanims, setStandaloneKazanims] = useState<Array<{ id: number; title: string }>>([]);
  const [standaloneUnitId, setStandaloneUnitId] = useState<number | ''>('');

  useEffect(() => {
    teacherApi.goalDefinitions()
      .then((r: any) => {
        const defs = r.data ?? [];
        setGoalDefs(defs);
        if (defs.length > 0) setGoalDefId(defs[0].id);
      })
      .catch(() => {});
    teacherApi.booksForAssignment()
      .then((r: any) => setBooks((r.data ?? []).map((b: any) => ({
        id: b.id,
        title: b.title,
        display_name: b.display_name,
        subjectId: b.subjectId ?? b.subject?.id ?? null,
        subject: b.subject ?? null,
      }))))
      .catch(() => setBooks([]));
  }, []);

  // Standalone ders → konu cascade.
  useEffect(() => {
    if (!standaloneSubjectId) { setStandaloneKazanims([]); setStandaloneUnitId(''); return; }
    setStandaloneUnitId('');
    teacherApi.kazanims(Number(standaloneSubjectId))
      .then((r) => setStandaloneKazanims(r.data ?? []))
      .catch(() => setStandaloneKazanims([]));
  }, [standaloneSubjectId]);

  // Book → sections + subject konuları cascade.
  useEffect(() => {
    if (!bookId) { setSectionOptions([]); setKazanims([]); setSectionLabel(''); setSubjectUnitId(''); return; }
    setSectionLabel('');
    setSubjectUnitId('');
    teacherApi.bookSections(bookId)
      .then((r: any) => {
        const all = r.data?.sections ?? [];
        const seen = new Set<string>();
        setSectionOptions(all.filter((s: any) => {
          if (!s.section_label || seen.has(s.section_label)) return false;
          seen.add(s.section_label);
          return true;
        }).map((s: any) => ({ section_label: s.section_label, section_display_label: s.section_display_label, section_category: s.section_category, question_count: s.question_count ?? null })));
      })
      .catch(() => setSectionOptions([]));
    const book = books.find((b) => b.id === Number(bookId));
    const sid = book?.subjectId ?? book?.subject?.id;
    if (sid) {
      teacherApi.kazanims(Number(sid)).then((r) => setKazanims(r.data ?? [])).catch(() => setKazanims([]));
    } else {
      setKazanims([]);
    }
  }, [bookId, books]);

  const submit = async () => {
    if (!goalDefId) { setError('Hedef türü seçin.'); return; }
    const toMin = (t: string) => Number(t.split(':')[0]) * 60 + Number(t.split(':')[1]);
    if (toMin(endTime) <= toMin(startTime)) { setError('Bitiş saati başlangıçtan sonra olmalı.'); return; }
    setSubmitting(true); setError(null);
    try {
      const homework = attachHw && bookId
        ? {
            title: goalDefs.find((g) => g.id === goalDefId)?.title ?? 'Hedef Ödevi',
            description: sectionLabel ? `Bölüm: ${sectionLabel}` : undefined,
            bookId: Number(bookId),
            sectionLabel: sectionLabel || undefined,
            dueDate: date,
          }
        : undefined;
      const sUnit = subjectUnitId ? Number(subjectUnitId) : standaloneUnitId ? Number(standaloneUnitId) : undefined;
      const sSubject = standaloneSubjectId
        ? Number(standaloneSubjectId)
        : bookId
          ? (books.find((b) => b.id === Number(bookId))?.subjectId ?? undefined)
          : undefined;
      const res = await teacherApi.assignGoalToClass(classId, Number(goalDefId), parseInt(target, 10) || 0, date, startTime, endTime, homework, sUnit, sSubject ?? undefined);
      const data = (res as any)?.data ?? res;
      setResult({
        succeeded: data?.succeeded ?? data?.created ?? 0,
        conflicts: data?.conflicts ?? [],
      });
      // Refresh the open student's program so a just-assigned goal shows up
      // immediately (matches HedefAtaModal's onSubmitted → loadProgram).
      onAssigned?.();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message ?? 'Atama başarısız.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div>
        <div className="mb-4 rounded-[12px] p-4 bg-green-50 border border-green-200">
          <p style={{ ...F, fontSize: 15, fontWeight: 700, color: '#27a745' }}>
            ✓ {result.succeeded} öğrenciye hedef atandı.
          </p>
        </div>
        {result.conflicts.length > 0 && (
          <div className="mb-4 rounded-[12px] p-4 bg-yellow-50 border border-yellow-200">
            <p style={{ ...F, fontSize: 14, fontWeight: 700, color: '#a97e00', marginBottom: 8 }}>
              {result.conflicts.length} öğrencide saat çakışması — bu öğrenciler atlandı:
            </p>
            <ul className="space-y-1">
              {result.conflicts.map((c, i) => (
                <li key={i} style={{ ...F, fontSize: 13, color: '#6b5300' }}>• {c.student}: {c.message}</li>
              ))}
            </ul>
          </div>
        )}
        <button onClick={onClose} className="w-full rounded-[12px] py-2.5 bg-[#3973bf] text-white font-bold hover:opacity-90">
          Kapat
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Hedef Türü</label>
        <select value={goalDefId} onChange={e => setGoalDefId(e.target.value ? Number(e.target.value) : '')}
          className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[14px]"
          style={{ border: '2px solid #3973bf', ...F }}>
          {goalDefs.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
        </select>
      </div>
      <div>
        <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Hedef Soru Sayısı</label>
        <input type="number" min={1} max={500} value={target} onChange={e => setTarget(e.target.value)}
          className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[16px] font-bold text-center"
          style={{ border: '2px solid #3973bf', ...F }} />
      </div>
      <div>
        <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Tarih</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[14px]"
          style={{ border: '2px solid #3973bf', ...F }} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Başlangıç</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
            className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
            style={{ border: '2px solid #3973bf', ...F }} />
        </div>
        <div>
          <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Bitiş</label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
            className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
            style={{ border: '2px solid #3973bf', ...F }} />
        </div>
      </div>

      {/* Ders & Konu — single-student Hedef Ata ile birebir */}
      {(() => {
        const subjectOptions = dedupSubjectOptionsFromBooks(books);
        const noSubjects = subjectOptions.length === 0;
        return (
          <div className="rounded-[12px] p-3" style={{ background: '#f4f6fa', border: '1.5px solid #e1e7f3' }}>
            <p style={{ ...F, fontSize: 13, fontWeight: 700, color: '#3973bf', marginBottom: 8 }}>Ders & Konu (opsiyonel)</p>
            <div className="space-y-2">
              <div>
                <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Ders</label>
                <select value={standaloneSubjectId} onChange={e => setStandaloneSubjectId(e.target.value ? Number(e.target.value) : '')}
                  disabled={noSubjects}
                  className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                  style={{ border: '1.5px solid #3973bf', ...F }}>
                  <option value="">{noSubjects ? 'Atanabilir kitap/ders bulunamadı' : 'Ders seçilmedi'}</option>
                  {subjectOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                  Konu {standaloneSubjectId ? '(opsiyonel)' : '— önce ders seçin'}
                </label>
                <select value={standaloneUnitId} onChange={e => setStandaloneUnitId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!standaloneSubjectId || standaloneKazanims.length === 0}
                  className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                  style={{ border: '1.5px solid #d8d8d8', ...F }}>
                  <option value="">Konu seçilmedi</option>
                  {standaloneKazanims.map(k => <option key={k.id} value={k.id}>{k.title}</option>)}
                </select>
              </div>
              {standaloneSubjectId && standaloneUnitId && (
                <p style={{ ...F, fontSize: 11, color: '#3973bf', fontWeight: 600 }}>
                  Hedef şu şekilde görünecek: {subjectOptions.find(s => s.id === Number(standaloneSubjectId))?.name} - {standaloneKazanims.find(k => k.id === Number(standaloneUnitId))?.title}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Opsiyonel kitap ödevi — single-student Hedef Ata ile birebir */}
      <div className="rounded-[12px] p-3" style={{ background: '#f4f6fa', border: '1.5px solid #e1e7f3' }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={attachHw} onChange={e => setAttachHw(e.target.checked)} />
          <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>Kitap ödevi de oluştur</span>
        </label>
        {attachHw && (
          <div className="mt-3 space-y-2">
            <div>
              <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Kitap</label>
              <select value={bookId} onChange={e => setBookId(e.target.value ? Number(e.target.value) : '')}
                className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px]"
                style={{ border: '1.5px solid #3973bf', ...F }}>
                <option value="">Kitap seçin</option>
                {books.map(b => <option key={b.id} value={b.id}>{b.display_name ?? b.title}</option>)}
              </select>
            </div>
            <div>
              <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                Test / Bölüm {bookId ? '(opsiyonel)' : '— önce kitap seçin'}
              </label>
              <select value={sectionLabel} onChange={e => {
                  const label = e.target.value;
                  setSectionLabel(label);
                  const sec = sectionOptions.find(s => s.section_label === label);
                  if (sec?.question_count) setTarget(String(sec.question_count));
                }}
                disabled={!bookId || sectionOptions.length === 0}
                className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                style={{ border: '1.5px solid #d8d8d8', ...F }}>
                <option value="">Tüm Kitap (test seçilmedi)</option>
                {sectionOptions.map(s => (
                  <option key={s.section_label} value={s.section_label}>
                    {s.section_display_label || s.section_label}{s.section_category ? ` - ${s.section_category}` : ''}{s.question_count ? ` (${s.question_count} soru)` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                Konu {bookId ? '(opsiyonel)' : '— önce kitap seçin'}
              </label>
              <select value={subjectUnitId} onChange={e => setSubjectUnitId(e.target.value ? Number(e.target.value) : '')}
                disabled={!bookId || kazanims.length === 0}
                className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                style={{ border: '1.5px solid #d8d8d8', ...F }}>
                <option value="">Konu seçilmedi</option>
                {kazanims.map(k => <option key={k.id} value={k.id}>{k.title}</option>)}
              </select>
            </div>
            <p style={{ ...F, fontSize: 11, color: '#888' }}>Ödev hem Çalışma Planı hem de Ödevlerim sayfasında görünür.</p>
          </div>
        )}
      </div>

      {error && <div className="rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{error}</div>}
      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="flex-1 rounded-[12px] py-2.5 hover:opacity-90" style={{ background: '#e5e5e5' }}>
          <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
        </button>
        <button onClick={submit} disabled={submitting}
          className="flex-1 rounded-[12px] py-2.5 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5599b' }}>
          <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
            {submitting ? 'Atanıyor…' : 'Tüm Sınıfa Ata'}
          </span>
        </button>
      </div>
    </div>
  );
};
